<template>
  <div class="c-machinedetail">
    <v-row>
      <v-col>
        <div class="section-box">
          <div class="section-text">En quelques chiffres</div>
          <period-selector
          ></period-selector>
        </div>
      </v-col>
    </v-row>

    <v-row no-gutters class="c-machinedetail__items" justify="center">
      <v-col cols="2" v-for="item in counts" :key="item.label">
        <overview-item
          :src="item.src"
          :label="item.label"
          :value="item.value"
        ></overview-item>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <div class="section-box">
          <div class="section-text">Informations sur cette machine</div>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-card light class="c-machinedetail__infos">
          <v-container>
            <v-row>
              <v-col cols="12" md="6" class="pa-0">
                N° de série : {{ item.serial_number }}
              </v-col>
              <v-col cols="12" md="6" class="pa-0">
                Client : {{ client.name }}
              </v-col>
              <v-col cols="12" md="6" class="pa-0">
                Type d'établissement : {{ client.contract | contract2text}}
              </v-col>
              <v-col cols="12" md="6" class="pa-0">
                Pays : {{ client.address.country }}
              </v-col>
              <v-col cols="12" md="6" class="pa-0">
                Ville : {{ client.address.city }}
              </v-col>
              <v-col cols="12" md="6" class="pa-0">
                Date d'installation : {{ item.installation_date | text2moment | moment2date }}
              </v-col>
              <v-col cols="12" md="6" class="pa-0">
                Date de dernière révision : {{ item.revision_date | text2moment | moment2datetime }}
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-col>
    </v-row>

    <v-row hidden-xs-only>
      <v-col cols="12">
        <div class="section-box">
          <div class="section-text">Les chiffres en détail</div>
        </div>

        <period-navigator>
          <wines-consumption-chart class="c-machinedetail__stats"
            :width="600"
            :height="300"
            :chartData="consumption"
            :options="{responsive: true, maintainAspectRatio: false}"
            :xLabel="xLabel"
            :styles="{height: '300px', position: 'relative'}"
          ></wines-consumption-chart>
          <revenue-chart class="c-machinedetail__stats"
            :width="600"
            :height="300"
            :chartData="revenue"
            :options="{responsive: true, maintainAspectRatio: false}"
            :xLabel="xLabel"
            :styles="{height: '300px', position: 'relative'}"
          ></revenue-chart>
        </period-navigator>
      </v-col>
    </v-row>

    <v-row hidden-xs-only>
      <v-col cols="12">
        <div class="section-box">
          <div class="section-text">La vinothèque de cette machine</div>
        </div>

        <div class="c-machinedetail__vino">
          <wine-item class="c-machinedetail__vino__item" v-for="item in ranking" :key="item.wine_id"
            :id="item.wine_id"
            :src="$store.getters.getLabelUrl(item)"
            :name="item.name"
            :producer="item.producer_name"
            :color="item.color"
            :revenue="item.revenue"
            :volume="item.volume"
          ></wine-item>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import axios from 'axios';
import { mapState } from 'vuex';

import OverviewItem from '@/components/OverviewItem';
import PeriodNavigator from '@/components/PeriodNavigator';
import PeriodSelector from '@/components/PeriodSelector';
import RevenueChart from '@/components/RevenueChart';
import WineItem from '@/components/WineItem';
import WinesConsumptionChart from '@/components/WinesConsumptionChart';

import { contract2text, dateTimeToChartLabel, moment2date, moment2datetime, periodToChartLabel, text2moment } from '@/filters';

import PeriodListener from '@/mixins/PeriodListener';

import {
  MACHINE_READ
} from '@/store/actions/machine';
import {
  STATS_GET_HISTORY,
  STATS_GET_COUNT,
  STATS_GET_WINE_RANKING
} from '@/store/actions/stats';
import { BOTTLE_VOLUME } from '@/store/modules/stats';


const DEFAULT_CONSUMPTION_CHART = {
  labels: [],
  datasets: [
    {
      label: 'Global',
      borderColor: '#7addff',
      fill: false,
      type: 'line'
    },
    {
      label: 'Vin rouge',
      backgroundColor: '#dd2222'
    },
    {
      label: 'Vin rosé',
      backgroundColor: '#f87979'
    },
    {
      label: 'Vin blanc',
      backgroundColor: '#fcffc6'
    }
  ]
};

const DEFAULT_REVENUE_CHART = {
  labels: [],
  datasets: [
    {
      label: 'Chiffre d\'affaires',
      borderColor: '#7addff'
    }
  ]
};

export default {
  name: 'MachineDetail',

  props: {
    machineId: Number
  },

  mixins: [
    PeriodListener
  ],

  components: {
    OverviewItem,
    PeriodNavigator,
    PeriodSelector,
    RevenueChart,
    WineItem,
    WinesConsumptionChart
  },

  filters: {
    contract2text,
    text2moment,
    moment2date,
    moment2datetime
  },

  data: () => ({
    loading: false
  }),

  computed: {
    ...mapState({
      item: state => state.machine.machine,
      client: state => state.client.client,
      counts: state => {
        const count = state.stats.count;

        return [
          {
            label: 'Litres consommés',
            src: require('@/assets/L-white-icon.png'),
            value: count.volume / 100
          },
          {
            label: 'Bouteilles',
            src: require('@/assets/wine_bottle_white_icon.png'),
            value: count.volume / BOTTLE_VOLUME
          },
          {
            label: 'Verres',
            src: require('@/assets/glass_white_icon.png'),
            value: count.glasses
          },
          {
            label: 'Carafes',
            src: require('@/assets/pitcher_white_icon.png'),
            value: count.pitchers
          },
          {
            label: 'Chiffre d\'affaires',
            src: require('@/assets/euro-icon.png'),
            value: count.revenue || NaN
          }
        ];
      },
      consumption: state => {
        const labels = [];
        const global = [];
        const red = [];
        const rose = [];
        const white = [];

        for (const d of state.stats.history) {
          labels.push(dateTimeToChartLabel(d.date, state.stats.period));
          global.push(d.volume / 100);
          red.push(d.red_volume / 100);
          rose.push(d.rose_volume / 100);
          white.push(d.white_volume / 100);
        }

        const data = Object.assign({}, DEFAULT_CONSUMPTION_CHART);
        data.labels = labels;
        data.datasets[0].data = global;
        data.datasets[1].data = red;
        data.datasets[2].data = rose;
        data.datasets[3].data = white;

        return data;
      },
      revenue: state => {
        const labels = [];
        const revenue = [];

        for (const d of state.stats.history) {
          labels.push(dateTimeToChartLabel(d.date, state.stats.period));
          revenue.push(d.revenue);
        }

        const data = Object.assign({}, DEFAULT_REVENUE_CHART);
        data.labels = labels;
        data.datasets[0].data = revenue;

        return data;
      },
      ranking: state => state.stats.wine_ranking,
      xLabel: state => {
        return periodToChartLabel(state.stats.period);
      }
    })
  },

  mounted () {
    this.fetchData();
  },

  watch: {
    'machineId': function () {
      this.fetchData();
    }
  },

  methods: {
    fetchData () {
      this.loading = true;

      axios.all([
        this.$store.dispatch(MACHINE_READ, this.machineId),
        this.$store.dispatch(STATS_GET_COUNT, { machine_id: this.machineId }),
        this.$store.dispatch(STATS_GET_HISTORY, { machine_id: this.machineId }),
        this.$store.dispatch(STATS_GET_WINE_RANKING, { machine_id: this.machineId })
      ])
        .finally(() => {
          this.loading = false;
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.c-machinedetail {
  &__items {
    text-align: center;
  }

  &__infos {
    background-color: lightgrey;
    color: #4a4a4a;

    &-elt {
      margin: 0 20px 0 20px;
      text-align: left;
      display: inline-block;
    }
  }

  &__vino {
    margin: auto;
    width: 450px;

    &__item {
      margin: 20px;
    }
  }
}
</style>
